// src/theme/demoView.ts
//
// "Leo Berwick view" — a demo switch that swaps the palette to the Leo Berwick
// brand colors (src/theme/theme.css) and hides FilePigeon branding and the Tax
// Toolbox.
//
// The switch exists only in builds made with REACT_APP_DEMO_VIEW=true. Staging's
// deploy.yml sets it; deploy-prod.yml does not, so production never shows the
// switch and ignores any preference a browser has stored.
//
// The preference is a display setting with no client data in it, so it is not
// engagement-scoped and is deliberately left out of clearEngagementState: the
// login page should keep the chosen view after sign-out.

import { useSyncExternalStore } from "react";

export const DEMO_VIEW_AVAILABLE = process.env.REACT_APP_DEMO_VIEW === "true";

const STORAGE_KEY = "__demoView";
const DEMO_TITLE = "FDD Toolbox";
const standardTitle = typeof document !== "undefined" ? document.title : "";

function readStored(): boolean {
  if (!DEMO_VIEW_AVAILABLE) return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "lb";
  } catch {
    return false;
  }
}

let enabled = readStored();
const listeners = new Set<() => void>();

function apply(): void {
  const rootEl = document.documentElement;
  if (enabled) rootEl.dataset.theme = "lb";
  else delete rootEl.dataset.theme;
  document.title = enabled ? DEMO_TITLE : standardTitle;
}

/** Call once before the first render so the login page already has the view. */
export function initDemoView(): void {
  apply();
}

export function setDemoView(on: boolean): void {
  if (!DEMO_VIEW_AVAILABLE) return;
  enabled = on;
  try {
    if (on) localStorage.setItem(STORAGE_KEY, "lb");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: the view still applies for this page load.
  }
  apply();
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True while the Leo Berwick view is on. Always false in builds without the switch. */
export function useDemoView(): boolean {
  return useSyncExternalStore(subscribe, () => enabled);
}
